import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEscPressed } from 'app/utils/accessibility/accessibilityUtils';
import { FormattedMessage } from 'react-intl';
import FacetValuesSection from './facetValuesSection';
import { SECTION_MAX_FACET_VALUES, SIZE_GROUP_ID, SUBSECTION_MAX_FACET_VALUES } from '../facetsListConstants';
import SizeFacetMediumDown from './sizeFacetMediumDown/sizeFacetMediumDown';
import mediaQueries from '../../../../utils/mediaQueries';

class Facet extends Component {
  constructor() {
    super();

    this.state = {
      isActive: !!mediaQueries.is_large_up(),
    };

    this.facetListItem = React.createRef();
    this.facetContainer = React.createRef();

    this.displayFacetValues = this.displayFacetValues.bind(this);
    this.onFacetItemClick = this.onFacetItemClick.bind(this);
    this.handleShowAllClick = this.handleShowAllClick.bind(this);
  }

  componentDidMount() {
    const { isMediumDown, hasAppliedItems } = this.props;

    this.setAccordionContentMaxHeight();

    if (hasAppliedItems) {
      this.setState({ isActive: isMediumDown });
    }

    if (!('ontouchstart' in window)) {
      this.facetListItem.current.addEventListener('mouseover', () => {
        const { isActive } = this.state;
        if (!isActive && !isMediumDown) this.setState({ isActive: true });
      });
    }

    this.facetListItem.current.addEventListener('mouseleave', () => {
      const { isActive } = this.state;
      if (isActive && !isMediumDown) this.setState({ isActive: false });
    });
  }

  componentDidUpdate() {
    this.setAccordionContentMaxHeight();
  }

  onFacetItemClick(item) {
    const { applyFacetItem, removeFacetItem } = this.props;
    if (item.selected) {
      removeFacetItem(item);
    } else {
      applyFacetItem(item);
    }
  }

  setAccordionContentMaxHeight() {
    const { isActive } = this.state;

    if (isActive) {
      const computedStyle = getComputedStyle(this.facetContainer.current);
      const accordionContent = this.facetContainer.current.parentElement;
      const facet = this.facetContainer.current.closest('.facet-navigation');
      const productList = this.facetContainer.current.closest('.product-list');

      const facetLocation = facet.getBoundingClientRect();
      const productListLocation = productList.getBoundingClientRect();

      accordionContent.style.maxHeight = `${
        this.facetContainer.current.offsetHeight +
        parseFloat(computedStyle.marginTop) +
        parseFloat(computedStyle.marginBottom)
      }px`;
      accordionContent.style.left = `${facetLocation.left < 0 ? 0 : facetLocation.left - productListLocation.left}px`;
    }
  }

  handleShowAllClick() {
    this.setAccordionContentMaxHeight();
  }

  displayFacetValues() {
    this.setState(({ isActive }) => {
      document.addEventListener(
        'keydown',
        e => {
          if (isEscPressed(e)) {
            this.setState({ isActive: false });
          }
        },
        { once: true }
      );

      return {
        isActive: !isActive,
      };
    });
  }

  render() {
    const { isActive } = this.state;
    const { facet, hasAppliedItems, isMediumDown } = this.props;

    const facetNavigationClass = isActive ? 'facet-navigation active' : 'facet-navigation';
    const facetContentClass = isActive
      ? 'content animate-opacity-increase'
      : 'content animate-opacity-decrease animate-height-decrease';
    const hasAppliedFilterClass = hasAppliedItems ? 'has-applied-filters' : '';

    const uniqueFacetTitles = [...new Set(facet.items.reduce((acc, current) => acc.concat([current.title]), []))];
    const facetsByTitleGroup = uniqueFacetTitles.map(title => facet.items.filter(size => size.title === title));

    if (facet.id === SIZE_GROUP_ID && isMediumDown)
      return (
        <SizeFacetMediumDown
          ref={{ facetContainer: this.facetContainer, facetListItem: this.facetListItem }}
          displayFacetValues={this.displayFacetValues}
          facet={facet}
          isActive={isActive}
          facetsByTitleGroup={facetsByTitleGroup}
          uniqueFacetTitles={uniqueFacetTitles}
          facetContentClass={facetContentClass}
          onFacetItemClick={this.onFacetItemClick}
          facetNavigationClass={facetNavigationClass}
          hasAppliedFilterClass={hasAppliedFilterClass}
          {...this.props}
        />
      );

    return (
      <li ref={this.facetListItem} className={`${facetNavigationClass} ${hasAppliedFilterClass}`}>
        <button
          type="button"
          className="facet-header"
          onClick={this.displayFacetValues}
          aria-controls={facet.id}
          aria-expanded={isActive}
        >
          <span className="button-content">
            {facet.name}
            <i className="fa-regular fa-chevron-down" />
          </span>
        </button>

        <div id={facet.id} className={facetContentClass} aria-hidden={!isActive}>
          {isActive && (
            <>
              {facet.id === SIZE_GROUP_ID && (
                <div className="show-for-large-up facet__size-container">
                  <span className="facet__size-indicator ">
                    <FormattedMessage id="search.result.facet.size.indicator" description="Size indicator" />
                  </span>
                </div>
              )}
              <div className="facet__item-section" ref={this.facetContainer}>
                {uniqueFacetTitles.length > 1 ? (
                  facetsByTitleGroup.map((group, key) => (
                    <FacetValuesSection
                      key={uniqueFacetTitles[key]}
                      chunkSize={SUBSECTION_MAX_FACET_VALUES}
                      facet={facet}
                      isActive={isActive}
                      sectionTitle={uniqueFacetTitles[key]}
                      onFacetItemClick={this.onFacetItemClick}
                      onShowAllClick={this.handleShowAllClick}
                      {...this.props}
                    />
                  ))
                ) : (
                  <FacetValuesSection
                    chunkSize={SECTION_MAX_FACET_VALUES}
                    facet={facet}
                    isActive={isActive}
                    onFacetItemClick={this.onFacetItemClick}
                    onShowAllClick={this.handleShowAllClick}
                    {...this.props}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </li>
    );
  }
}

Facet.defaultProps = {
  isMediumDown: false,
};

Facet.propTypes = {
  facet: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        uid: PropTypes.string,
        count: PropTypes.number,
        name: PropTypes.string,
        title: PropTypes.string,
        facetImage: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  applyFacetItem: PropTypes.func.isRequired,
  removeFacetItem: PropTypes.func.isRequired,
  isMediumDown: PropTypes.bool,
  hasAppliedItems: PropTypes.bool.isRequired,
};

export default Facet;
